import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { 
  ChatBubbleLeftIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

/**
 * ReplyActivityItem component for displaying a user's forum reply
 * Shows reply content preview, timestamp, and engagement metrics
 */
const ReplyActivityItem = React.memo(({ reply }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    const plainText = text.replace(/<[^>]*>/g, ''); // Remove HTML tags
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover:shadow-sm">
      {/* Reply Icon */}
      <div className="flex-shrink-0 mt-0.5">
        <ChatBubbleLeftIcon className="w-4 h-4 text-blue-500" />
      </div>

      {/* Reply Content */}
      <div className="flex-1 min-w-0">
        {/* Activity Type and Timestamp */}
        <div className="flex items-center space-x-2 mb-1">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Replied
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {formatDate(reply.createdAt)}
          </span>
        </div>

        {/* Reply Content */}
        <Link
          to={`/forum/${reply.questionId}`}
          className="block hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-200"
        >
          <p className="text-sm text-gray-900 dark:text-white line-clamp-2">
            {truncateText(reply.content)}
          </p>
        </Link>

        {/* Engagement Metrics */}
        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {/* Like Count */}
          <div className="flex items-center space-x-1">
            {reply.likeCount > 0 ? (
              <HeartIconSolid className="w-3 h-3 text-red-500" />
            ) : (
              <HeartIcon className="w-3 h-3" />
            )}
            <span>{reply.likeCount || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

ReplyActivityItem.displayName = 'ReplyActivityItem';

export default ReplyActivityItem;